import { useCallback, useEffect, useRef, useState } from "react";

import { ITEMS_PAGE_SIZE } from "../constants/payoutBatches.constants";
import { PayoutBatchesService } from "../services/payoutBatches.service";
import type {
  ListPayoutBatchItemsParams,
  PayoutBatchItem,
  PayoutBatchItemStatus,
} from "../types/payoutBatches.types";
import type { BatchRowUpdate, BatchRowUpdateListener } from "./usePayoutBatchLive";

export type ItemsQuery = Pick<ListPayoutBatchItemsParams, "status" | "hasErrors" | "hasWarnings">;

export interface UsePayoutBatchItemsResult {
  rows: PayoutBatchItem[];
  total: number;
  /** First read of the current query — the table shows its skeleton. Refreshes never blank the rows. */
  isLoading: boolean;
  isRefreshing: boolean;
  hasError: boolean;
  refresh: () => Promise<void>;
  /** Sets rows' statuses in place, for a mutation whose outcome is already known. */
  patchStatuses: (statuses: Record<string, PayoutBatchItemStatus>) => void;
}

interface PageState {
  key: string;
  rows: PayoutBatchItem[];
  total: number;
}

/**
 * One server-side page of a batch's rows. Re-reads in the foreground when the query changes, in the
 * background when the batch `revision` moves (a full batch read, an adopted mutation), and patches a row's
 * status in place from the socket's `last_item` so a run reads live without a request per row.
 */
export function usePayoutBatchItems(
  batchId: string,
  query: ItemsQuery,
  page: number,
  revision: number,
  subscribeRowUpdates?: (listener: BatchRowUpdateListener) => () => void,
): UsePayoutBatchItemsResult {
  const queryKey = [
    batchId,
    query.status ?? "*",
    query.hasErrors ?? "*",
    query.hasWarnings ?? "*",
    page,
  ].join("|");

  const [pageState, setPageState] = useState<PageState | null>(null);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const requestSeq = useRef(0);

  const fetchPage = useCallback(
    async (background: boolean) => {
      if (!batchId) return;
      const seq = ++requestSeq.current;
      if (background) setIsRefreshing(true);
      try {
        const { data } = await PayoutBatchesService.listItems(batchId, {
          skip: (page - 1) * ITEMS_PAGE_SIZE,
          limit: ITEMS_PAGE_SIZE,
          status: query.status,
          hasErrors: query.hasErrors,
          hasWarnings: query.hasWarnings,
        });
        if (seq !== requestSeq.current) return;
        setPageState({ key: queryKey, rows: data.data, total: data.total });
        setHasError(false);
        setSettledKey(queryKey);
      } catch {
        if (seq !== requestSeq.current) return;
        if (!background) {
          setHasError(true);
          setPageState({ key: queryKey, rows: [], total: 0 });
          setSettledKey(queryKey);
        }
      } finally {
        if (seq === requestSeq.current) setIsRefreshing(false);
      }
    },
    [batchId, page, query.hasErrors, query.hasWarnings, query.status, queryKey],
  );

  useEffect(() => {
    void fetchPage(false);
  }, [fetchPage]);

  const seenRevision = useRef(revision);
  useEffect(() => {
    if (seenRevision.current === revision) return;
    seenRevision.current = revision;
    void fetchPage(true);
  }, [revision, fetchPage]);

  useEffect(() => {
    if (!subscribeRowUpdates) return;
    return subscribeRowUpdates((update: BatchRowUpdate) => {
      setPageState((prev) => {
        if (!prev || !prev.rows.some((row) => row.id === update.id)) return prev;
        return {
          ...prev,
          rows: prev.rows.map((row) =>
            row.id === update.id
              ? {
                  ...row,
                  status: update.status as PayoutBatchItemStatus,
                  failure_code: update.failure_code ?? row.failure_code,
                }
              : row,
          ),
        };
      });
    });
  }, [subscribeRowUpdates]);

  const patchStatuses = useCallback((statuses: Record<string, PayoutBatchItemStatus>) => {
    setPageState((prev) =>
      prev
        ? { ...prev, rows: prev.rows.map((row) => ({ ...row, status: statuses[row.id] ?? row.status })) }
        : prev,
    );
  }, []);

  const current = pageState?.key === queryKey ? pageState : null;
  const refresh = useCallback(() => fetchPage(true), [fetchPage]);

  return {
    rows: current?.rows ?? [],
    total: current?.total ?? 0,
    isLoading: settledKey !== queryKey,
    isRefreshing,
    hasError,
    refresh,
    patchStatuses,
  };
}
